import React, { useState, useContext } from 'react';
import {
    IconButton,
    Badge,
    Menu,
    MenuItem,
    Typography,
    Box,
    Divider,
    Button,
    Tooltip,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    ListItemSecondaryAction,
    CircularProgress
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationContext from '../../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

const NotificationMenu = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        deleteNotification
    } = useContext(NotificationContext);
    const navigate = useNavigate();

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleMarkAllAsRead = async () => {
        await markAllAsRead();
    };

    const handleNotificationClick = async (notification) => {
        // If notification is not read, mark it as read
        if (!notification.read) {
            await markAsRead(notification._id);
        }

        // Navigate to the link if provided
        if (notification.link) {
            navigate(notification.link);
        }

        handleCloseMenu();
    };

    const handleDeleteNotification = async (event, id) => {
        event.stopPropagation(); // Prevent the notification click handler from firing
        await deleteNotification(id);
    };

    // Get appropriate icon based on notification type
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return <CheckCircleIcon sx={{ color: 'success.main' }} />;
            case 'error':
                return <ErrorIcon sx={{ color: 'error.main' }} />;
            case 'warning':
                return <WarningIcon sx={{ color: 'warning.main' }} />;
            case 'info':
            default:
                return <InfoIcon sx={{ color: 'info.main' }} />;
        }
    };

    // Format notification date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;

        // Less than a day
        if (diff < 24 * 60 * 60 * 1000) {
            const hours = Math.floor(diff / (60 * 60 * 1000));
            if (hours < 1) {
                const minutes = Math.floor(diff / (60 * 1000));
                return minutes <= 1 ? 'Just now' : `${minutes} minutes ago`;
            }
            return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
        }

        // Less than a week
        if (diff < 7 * 24 * 60 * 60 * 1000) {
            const days = Math.floor(diff / (24 * 60 * 60 * 1000));
            return `${days} ${days === 1 ? 'day' : 'days'} ago`;
        }

        // Format as date
        return date.toLocaleDateString();
    };

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton
                    size="large"
                    aria-label="show notifications"
                    aria-controls="notification-menu"
                    aria-haspopup="true"
                    onClick={handleOpenMenu}
                    color="inherit"
                    sx={{ mx: 1 }}
                >
                    <Badge badgeContent={unreadCount} color="error">
                        {unreadCount > 0 ? <NotificationsActiveIcon /> : <NotificationsIcon />}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                id="notification-menu"
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                sx={{
                    mt: 1,
                    '& .MuiMenu-paper': {
                        width: 360,
                        maxHeight: 400
                    }
                }}
            >
                <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        Notifications
                        {unreadCount > 0 && ` (${unreadCount} unread)`}
                    </Typography>
                    {unreadCount > 0 && (
                        <Button
                            size="small"
                            startIcon={<MarkEmailReadIcon />}
                            onClick={handleMarkAllAsRead}
                            sx={{ mr: -1 }}
                        >
                            Mark all as read
                        </Button>
                    )}
                </Box>
                <Divider />

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : notifications.length > 0 ? (
                    <List sx={{ p: 0 }}>
                        {notifications.map((notification) => (
                            <ListItem
                                key={notification._id}
                                button
                                onClick={() => handleNotificationClick(notification)}
                                alignItems="flex-start"
                                sx={{
                                    backgroundColor: notification.read ? 'inherit' : 'action.hover',
                                    borderLeft: notification.read ? 'none' : '4px solid',
                                    borderLeftColor: `${notification.type}.main`,
                                    transition: 'background-color 0.2s',
                                    '&:hover': {
                                        backgroundColor: 'action.selected'
                                    }
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: `${notification.type}.light` }}>
                                        {getNotificationIcon(notification.type)}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle2" noWrap>
                                            {notification.title || 'Notification'}
                                        </Typography>
                                    }
                                    secondary={
                                        <>
                                            <Typography variant="body2" color="text.primary" noWrap>
                                                {notification.message}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(notification.createdAt)}
                                            </Typography>
                                        </>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <IconButton edge="end" onClick={(e) => handleDeleteNotification(e, notification._id)} size="small">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No notifications to display
                        </Typography>
                    </Box>
                )}
            </Menu>
        </>
    );
};

export default NotificationMenu; 